import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { IVideoEntry } from "@components/videos/models/IVideoEntry";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { ReasonCode } from "@enums/ReasonCode";
import { SortDirection } from "@enums/SortDirection";
import { IDateParameters } from "@interfaces/IDateParameters";
import { IDriver } from "@interfaces/IDriver";
import { IJourney, IJourneyModel } from "@interfaces/IJourney";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { ISensorData } from "@interfaces/ISensorData";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleTrailerMapping } from "@interfaces/IVehicleTrailerMapping";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { IVehicleFilter } from "@interfaces/filters/IVehicleFilter";
import { DateService, DateUnit } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { IPolyline } from "@typings/google.maps.angular";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { ColourService } from "./colour.service";
import { DriverService } from "./driver.service";
import { DriverAssignmentService } from "./driverAssignment.service";
import { EventService, IEvent } from "./event.services";
import { EventMarkerType, GoogleMarkerService } from "./googleMarker.service";
import { MapService } from "./map.service";
import { PlotService } from "./plot.service";
import { PlotEventService } from "./plotEvent.service";
import { VehicleService } from "./vehicle.service";
import { VehicleInputService } from "./vehicleInput.service";
import { VeriDate } from "./veriDate.service";


export interface IHistoricalDay {
    date: Date;
    journeyModel: IJourneyModel;
    plots: IPlot[];
    events: IPlotEvent[];
}

export interface IVehicleJourneyByDateMap {
    journeys: IJourney[];
    date: Date;
}

export interface IVehicleJourneysMap {
    id: number;
    vehicle: IVehicle;
    journeysByDay: IVehicleJourneyByDateMap[];
    journeys: IJourney[];
    visibleJourneys: IJourney[];
    megaJourney?: IJourney;
    journeyCount: number;

    selected?: boolean;
}

export class JourneyService {
    private readonly videoEntryService = new HybridServiceAccessor<VideoEntryService>("videoEntryService", this.$injector);

    // https://flatuicolors.com/
    //private journeyColors = [
    //    "#3498db", // Blue (Light Pastel) #2980b9
    //    "#8e44ad", // Purple
    //    "#e74c3c", // Pink (Rusty / Salmon)
    //    "#16a085", // Green (Pastel)
    //    "#e67e22", // Orange
    //    //"#9d6079", // Purple (Dark)
    //    "#34495e", // Grey (Dark bluey)
    //    //"#7f8c8d", // Silver
    //    "#27ae60" // Green (Pea)
    //];

    static $inject = ["$q", "$http", "$log", "plotService", "driverAssignmentService", "plotEventService", "googleMarkerService", "vehicleService", "vehicleInputService", "driverService", "mapService", "colourService", "eventService", "veriDate", "translationService", "dateService", "$injector"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly $log: angular.ILogService,
        private readonly plotService: PlotService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly plotEventService: PlotEventService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly vehicleService: VehicleService,
        private readonly vehicleInputService: VehicleInputService,
        private readonly driverService: DriverService,
        private readonly mapService: MapService,
        private readonly colourService: ColourService,
        private readonly eventService: EventService,
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService,
        private readonly dateService: DateService,
        private readonly $injector: angular.auto.IInjectorService,
    ) {

        //this.test();
    }

    getIntersectingJourneys(start: moment.Moment, end: moment.Moment, bounds: google.maps.LatLngBounds): angular.IPromise<IJourney[]> {

        const latLongs: number[][] = [];

        latLongs.push([bounds.getNorthEast().lat(), bounds.getNorthEast().lng()]);
        latLongs.push([bounds.getNorthEast().lat(), bounds.getSouthWest().lng()]);
        latLongs.push([bounds.getSouthWest().lat(), bounds.getSouthWest().lng()]);
        latLongs.push([bounds.getSouthWest().lat(), bounds.getNorthEast().lng()]);
        latLongs.push([bounds.getNorthEast().lat(), bounds.getNorthEast().lng()]);

        var params = {
            start: start.toISOString(),
            end: end.toISOString(),
            //vehicleId: 0,
            latLongs: latLongs
        };

        return this
            .$http({
                url: `/api/journeys/intersecting`,
                method: "POST",
                //cache: true,
                data: params,
                //params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IJourney[]>) => {
                //this.processJourneys(response.data);

                response.data.sort((x, y) => {
                    return x.Id - y.Id;
                });

                return response.data;
            });
    }

    getCurrentJourney(vehicleId: number, invalidateCache = false): angular.IPromise<IJourney> {
        return this
            .$http({
                url: `/api/journeys/${vehicleId}/current`,
                method: "GET",
                cache: true,
                params: { minsToLive: 1, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IJourney>) => {
                var journey = response.data;

                if (journey) {
                    // This is needed so AngularJs knows when to update cards
                    journey.Id = this.randomNumber(1, 10000000000);
                    journey.VehicleId = vehicleId;

                    // Color current journey with first color by default
                    journey.lineColor = this.colourService.getColour(0, true).solidColour;
                    journey.isCurrent = true;

                    this.processJourney(journey);
                }

                return journey;
            });
    }

    getAllJourneys(startDate: Date, endDate?: Date, includeLinePoints = true, includeFormattedAddress = false, useCache = true, includeCan = false): angular.IPromise<IJourney[]> {
        const dateRange = this.getJourneyDateRange(startDate, endDate) as any;
        dateRange.includeLinePoints = includeLinePoints;
        dateRange.includeFormattedAddress = includeFormattedAddress;
        dateRange.includeCan = includeCan;

        return this
            .$http({
                method: "GET",
                url: `/api/journeys/`,
                cache: useCache,
                params: dateRange
            })
            .then((result: angular.IHttpResponse<IJourney[]>) => {
                var journeys = result.data;

                journeys = this.filterSmallJourneys(journeys);
                this.processJourneys(journeys);

                return journeys;
            })
            .catch((errorMessage: any) => {

                this.$log.error("Get journeys error", errorMessage);
                return null;
            });
    }

    getJourneysForDateAndVehicle(vehicleId: number, selectedDate: Date, endDate: Date = null, includeCurrent = false, invalidateCache = false): angular.IPromise<IJourney[]> {
        let currentJourneyPromise: angular.IPromise<IJourney> = this.$q.when(null);
        if (includeCurrent) {
            currentJourneyPromise = this.getCurrentJourney(vehicleId, invalidateCache);
        }

        const journeysPromise = this.getJourneysForVehicle(vehicleId, selectedDate, endDate, invalidateCache);

        return this.$q.all([journeysPromise, currentJourneyPromise])
            .then(([journeys, currentJourney]) => {
                if (currentJourney) {
                    journeys.push(currentJourney);
                }

                journeys.sort((a, b) => this.veriDate.sort(a.StartUtc, b.StartUtc, SortDirection.Ascending));

                this.processJourneys(journeys);
                return journeys;
            })
            .catch((errorMessage: any) => {

                this.$log.error("Get journeys for vehicle error", errorMessage);
                return [];
            });
    }

    getJourneysForVehicle(vehicleId: number, startDate: Date, endDate: Date = null, invalidateCache = false): angular.IPromise<IJourney[]> {

        const dateRange = this.getJourneyDateRange(startDate, endDate) as any;

        dateRange.minsToLive = 5;
        dateRange.invalidateCache = invalidateCache;

        return this
            .$http({
                method: "GET",
                url: `/api/journeys/${vehicleId}/`,
                cache: true,
                params: dateRange
            })
            .then((result: angular.IHttpResponse<IJourney[]>) => {
                var journeys = result.data;
                journeys = this.filterSmallJourneys(journeys);

                journeys.sort((a, b) => this.veriDate.sort(a.StartUtc, b.StartUtc, SortDirection.Ascending));
                this.processJourneys(journeys);
                return journeys;
            }).catch((errorMessage: any) => {

                this.$log.error("Get journeys for vehicle error", errorMessage);
                return null;
            });
    }

    getSensorsDataForVehicle(vehicle: IVehicle, selectedDate: Date): angular.IPromise<ISensorData[]> {

        if (!vehicle) {
            console.error("No vehicle selected");
        }

        const vehicleId = vehicle.Id;
        const dateRange = this.getJourneyDateRange(selectedDate);
        var rangeStart = moment(dateRange.start);
        var rangeEnd = moment(dateRange.end);

        return this.plotService.getSensorsData(vehicleId, rangeStart, rangeEnd);
    }

    getAllJourneyDataForVehicle(vehicle: IVehicle, selectedDate: Date, getCurrentJourney: boolean = false): angular.IPromise<IHistoricalDay> {
        if (!vehicle) {
            console.error("No vehicle selected");
        }

        var includeCurrentJourney = getCurrentJourney && moment(selectedDate).isSame(moment(), "d");

        const dateRange = this.getJourneyDateRange(selectedDate);
        var rangeStart = moment(dateRange.start);
        var rangeEnd = moment(dateRange.end);

        return this.getHistoricalDayForVehicle(vehicle, rangeStart, rangeEnd, includeCurrentJourney)
    }

    getHistoricalDayForVehicle(vehicle: IVehicle, startDate: moment.Moment, endDate: moment.Moment, includeCurrentJourney: boolean = false): angular.IPromise<IHistoricalDay> {

        const vehicleId = vehicle.Id;

        const journeyPromise = this.getJourneysForDateAndVehicle(vehicleId, startDate.toDate(), endDate.toDate(), includeCurrentJourney)

        return journeyPromise.then((journeys: IJourney[]) => {

            const lastJourneyEndDate: Date = this.getLastJourneyEndUtcDate(journeys);
            const plotsEndDate = this.veriDate.createMoment(this.getJourneyDateRange(startDate.toDate(), lastJourneyEndDate).end);

            const vehicleInputPromise = this.vehicleInputService.getVehicleInputs();
            const probesPromise = this.vehicleService.getVehicleProbes(vehicle);
            const plotPromise = this.plotService.getPlotsForDateRange(vehicleId, startDate, plotsEndDate);
            const assignmentPromise = this.driverAssignmentService.getVehicleDriverAssignmentsForDateRange(vehicleId, startDate, endDate);
            const videoPromise = this.videoEntryService.service.getVideosEntriesForVehicle(vehicleId, startDate.toDate(), endDate.toDate(), { createFileUrls: true, invalidateCache: true }) as angular.IPromise<IVideoEntry[]>;
            const commonEventPromise = this.eventService.getEventsForVehicle(vehicle.Id, startDate, endDate);
            const trailerAssociationPromise = this.vehicleService.getVehicleTrailerMappings(vehicle.Id, startDate, endDate);
            const tempRecordPromise = this.vehicleService.getVehicleTemperatureRecords(vehicle.Id, startDate, endDate);
            const sensorsData = this.plotService.getSensorsData(vehicleId, startDate, endDate);

            return this.$q
                .all([plotPromise, assignmentPromise, vehicleInputPromise, probesPromise, videoPromise, commonEventPromise, trailerAssociationPromise, tempRecordPromise, sensorsData])
                .then(([plots, assignments, vehicleInputs, probes, videos, commonEvents, trailerAssociations, tempRecords, sensorsData]) => {

                    this.attachSensorDataToPlots(plots, sensorsData);

                    const hasCANPTOEvents = this.plotEventService.hasCANPTOEvents(plots);

                    const allJourneyEvents = this.plotEventService.parsePlotEvents(plots, assignments, vehicleInputs, videos, !hasCANPTOEvents, startDate, endDate);

                    this.createAdditionalJourneyEvents(allJourneyEvents, vehicle, plots, commonEvents, trailerAssociations);

                    const mapDataToJourneyPromises: angular.IPromise<void>[] = [];

                    journeys.forEach((journey) => {
                        mapDataToJourneyPromises.push(this.mapDataToJourney(journey, plots, allJourneyEvents));
                    });


                    // Start journey at 14:44
                    // And I have a video event at 14:40 - 14:50
                    // then merge the start event with the video event into one event that can be played.
                    // var matchingVideo = this.findMatchingVideoEvent(event.Utc, videos)
                    // if(mathcingVideo) { event.video = matchingVideo; }

                    return this.$q.all(mapDataToJourneyPromises).then(() => {
                        allJourneyEvents.sort((a, b) => {
                            var isSameTime = a.plot.UtcMoment.isSame(b.plot.UtcMoment);
                            if (isSameTime) {
                                // If plot the same, push end points behind
                                if (a.label.indexOf("Journey End") >= 0) {
                                    return 1;
                                }
                                else {
                                    return -1;
                                }
                            }

                            return a.plot.UtcMoment.isAfter(b.plot.UtcMoment) ? 1 : -1;
                        });

                        plots.forEach(plot => {
                            plot.TemperatureRecords = this.vehicleService.findTempRecordsForPlot(tempRecords, plot.Id);
                        });

                        allJourneyEvents.forEach(journeyEvent => {
                            const matchingVideo = this.findMatchingVideo(journeyEvent.Utc, videos);

                            if (matchingVideo) {
                                journeyEvent.video = matchingVideo;
                            }
                        });

                        var historicalDay: IHistoricalDay = {
                            date: startDate.toDate(),
                            journeyModel: this.createJourneyModel(journeys),
                            plots: plots,
                            events: allJourneyEvents
                        };

                        return historicalDay;
                    });
                });
        });
    }

    private findMatchingVideo(eventTime: string | Date, videos: IVideoEntry[]): IVideoEntry {
        let matchingVideo: IVideoEntry;
        videos.forEach(video => {
            if (moment(video.VideoStartTime).isSameOrBefore(eventTime) && moment(video.VideoEndTime).isSameOrAfter(eventTime)) {
                matchingVideo = video;
            }
        });

        return matchingVideo;
    }

    private findJourneyDriver(plots: IPlot[]): number {
        var driverCount: number[] = [];

        const escapeCount = plots.length / 2;

        for (var i = 0; i < plots.length; i++) {
            const plot = plots[i];

            if (!plot.DriverId) {
                continue;
            }

            if (!driverCount[plot.DriverId]) {
                driverCount[plot.DriverId] = 0;
            }

            if (++driverCount[plot.DriverId] >= escapeCount) {
                return plot.DriverId;
            }
        }

        var first = Object.keys(driverCount).sort()[0];
        //console.log("first", first, driverCount);
        return parseInt(first);
    }

    getJourneysForDriverSlim(driverId: number, startDate: moment.Moment, endDate: moment.Moment): angular.IPromise<IJourney[]> {
        const dateRange: IDateParameters = {
            start: startDate.startOf("d").toISOString(),
            end: endDate.endOf("d").toISOString()
        };

        return this
            .$http({
                method: "GET",
                url: `/api/drivers/${driverId}/journeys/`,
                params: dateRange
            })
            .then((result: angular.IHttpResponse<IJourney[]>) => {

                return result.data;
            })
            .catch((errorMessage: any) => {

                this.$log.error("Journey fetchAndFill error", errorMessage);
                return null;
            });
    }

    getJourneysForDriver(driverId: number, startDate: Date, endDate: Date = null, invalidateCache = false): angular.IPromise<IJourney[]> {

        const dateRange = this.getJourneyDateRange(startDate, endDate) as any;

        dateRange.minsToLive = 5;
        dateRange.invalidateCache = invalidateCache;

        return this
            .$http({
                method: "GET",
                url: `/api/drivers/${driverId}/journeys`,
                cache: true,
                params: dateRange
            })
            .then((result: angular.IHttpResponse<IJourney[]>) => {
                var journeys = result.data;
                journeys = this.filterSmallJourneys(journeys);
                this.processJourneys(journeys);
                return journeys;
            }).catch((errorMessage: any) => {

                this.$log.error("Get journeys for driver error", errorMessage);
                return null;
            });
    }

    getJourneysForDateAndDriver(driverId: number, selectedDate: Date, endDate: Date = null): angular.IPromise<IJourney[]> {
        const dateRange = this.getJourneyDateRange(selectedDate, endDate);

        return this
            .$http({
                method: "GET",
                url: `/api/drivers/${driverId}/journeys/`,
                params: dateRange
            })
            .then((result: angular.IHttpResponse<IJourney[]>) => {

                return result.data;
            })
            .catch((errorMessage: any) => {

                this.$log.error("Journey fetchAndFill error", errorMessage);
                return null;
            });
    }

    getJourneyDataForDriver(driverId: number, selectedDate: Date, endDate: Date = null): angular.IPromise<IHistoricalDay> {
        return this.getJourneysForDateAndDriver(driverId, selectedDate, endDate).then(journeys => {
            var plotPromises: angular.IPromise<IPlot[]>[] = [];
            var sensorDataPromises: angular.IPromise<ISensorData[]>[] = [];

            journeys.forEach(journey => {
                const rangeStart = moment(journey.StartUtc);
                const rangeEnd = moment(journey.EndUtc).add(1, "m");
                plotPromises.push(this.plotService.getPlotsForDateRange(journey.VehicleId, rangeStart, rangeEnd));
                sensorDataPromises.push(this.plotService.getSensorsData(journey.VehicleId, rangeStart, rangeEnd));
            });

            // const sensorsData = this.plotService.getSensorsData(vehicleId, rangeStart, rangeEnd);

            return this.$q.all({ plots: this.$q.all(plotPromises), sensorsData: this.$q.all(sensorDataPromises) }).then((results) => {
                var allPlots: IPlot[] = [];
                var allSensorsData: ISensorData[] = [];

                results.plots.forEach(plots => {
                    allPlots = allPlots.concat(plots);
                });

                results.sensorsData.forEach(sd => {
                    allSensorsData = allSensorsData.concat(sd);
                })

                this.attachSensorDataToPlots(allPlots, allSensorsData);

                journeys.forEach((journey) => {
                    journey.plots = this.plotService.slicePlotArrayByTime(allPlots, journey.StartUtc, journey.EndUtc);
                    journey.events = this.plotEventService.parsePlotEvents(journey.plots, [], [], [], false);
                });

                this.processJourneys(journeys);

                return {
                    date: selectedDate,
                    journeyModel: this.createJourneyModel(journeys),
                    plots: allPlots,
                    events: null
                };
            });
        });
    }

    filterSmallJourneys(journeys: IJourney[]): IJourney[] {

        // Remove any journeys that are 0 length or 0 minutes
        var filtered = journeys.filter(journey => !(journey.Distance < 100 || moment(journey.EndUtc).diff(journey.StartUtc, "m") < 1));
        return filtered;
    }

    processJourneys(journeys: IJourney[]) {
        var previousJourney: IJourney;

        var vehicleJourneyCount: number[] = [];

        performance.mark('processJourneys');
        for (var i = 0; i < journeys.length; i++) {
            const journey = journeys[i];

            // Color the journey (by vehicle)
            if (!vehicleJourneyCount[journey.VehicleId]) {
                vehicleJourneyCount[journey.VehicleId] = 0;
            }

            this.colorJourney(journey, vehicleJourneyCount[journey.VehicleId]++);

            this.processJourney(journey, previousJourney);

            previousJourney = journey;
        }

        performance.measure('processJourneys');
    }

    processJourney(journey: IJourney, previousJourney?: IJourney) {
        // Convert from metres into km
        journey.Distance = journey.Distance / 1000;

        if (!journey.isCurrent) {
            const ms = this.veriDate.createMoment(journey.EndUtc).diff(this.veriDate.createMoment(journey.StartUtc));
            const duration: any = moment.duration(ms);

            if (duration.hours() >= 1) {
                journey.duration = duration.format("h[hrs], m[m]");
            }
            else {
                journey.duration = duration.format("m [mins]");
            }
            journey.durationMoment = duration;
        }

        // Now calculate time difference from now until next journey
        if (previousJourney) {
            const ms = this.dateService.getDiff(previousJourney.EndUtc, journey.StartUtc, DateUnit.Ms);

            previousJourney.timeToNextJourney = "0 minutes";

            if (ms > 0) {
                previousJourney.timeToNextJourney = this.dateService.formatDuration(previousJourney.EndUtc, journey.StartUtc)
            }
        }

        if (journey.PointsEncoded && !journey.LinePoints) {
            journey.LinePoints = this.mapService.getPointsFromEncodedString(journey.PointsEncoded);
            //journey.LinePoints = this.mapService.catmullRomSpline(journey.LinePoints);
        }
        //else {
        //    journey.LinePoints = journey.LinePoints||[];
        //}
    }

    createJourneyModel(journeys: IJourney[]): IJourneyModel {
        var totalElapsedTime = 0;
        var journeyModel: IJourneyModel = {
            journeys: journeys,
            events: [],
            totalDistance: 0,
            totalDistanceKm: 0
        };

        journeys.forEach(journey => {
            journeyModel.totalDistance += journey.Distance ?? 0;
            journeyModel.totalDistanceKm += journey.DistanceKm ?? 0;
            totalElapsedTime += this.veriDate.createMoment(journey.EndUtc).diff(this.veriDate.createMoment(journey.StartUtc));

            journey.events.forEach(event => event.lineColor = journey.lineColor);

            journeyModel.events.push.apply(journeyModel.events, journey.events);
        });

        journeyModel.events.sort((a, b) => {
            return moment(a.plot.UtcMoment).isBefore(b.plot.UtcMoment) ? -1 : 1;
        });

        const d = moment.duration(totalElapsedTime);
        journeyModel.totalElapsedTime = (d as any).format("h [hours], m [mins]");

        return journeyModel;
    }

    // Shouldnt be needed anymore
    //setMapUrls(journeys: IJourney[], plots: IPlot[], size?: string) {

    //    journeys.forEach(j => {
    //        this.setMapUrl(j, plots, size);
    //    });
    //}

    //setMapUrl(journey: IJourney, plots: IPlot[], size = "278x133") {

    //    const filteredPlots = this.filterPlotsForJourney(journey, plots);
    //    const latLngs = this.createLatLngsForPlots(filteredPlots);
    //    let polylineColor = journey.lineColor || "0x00426aff";
    //    polylineColor = polylineColor.replace("#", "0x") + "FF";

    //    const endColour = journey.isCurrent ? "0x1e90ff" : "0xcf4946";
    //    // This gets around problems with the processor
    //    const startLat = journey.StartLatitude || filteredPlots[0].Latitude;
    //    const startLng = journey.StartLongitude || filteredPlots[0].Longitude;
    //    const endLat = journey.EndLatitude || filteredPlots[filteredPlots.length - 1].Latitude;
    //    const endLng = journey.EndLongitude || filteredPlots[filteredPlots.length - 1].Longitude;

    //    const markers = `markers=color:0x209058|${startLat},${startLng}&markers=color:${endColour}|${endLat},${endLng}`;

    //    this.googleService
    //        .getPolylineMapUrl(latLngs, polylineColor, markers, size)
    //        .then((mapUrl: string) => {
    //            journey.mapImageUrl = mapUrl;
    //        });
    //}

    createJourneyPolyline(journey: IJourney, journeyEvents?: any, dashed?: boolean): IPolyline {
        let latLngArray: google.maps.LatLng[] = null;

        //if (journey.SnappedLinePoints && journey.SnappedLinePoints.length !== 0) {
        //    latLngArray = this.plotService.convertArrayToGoogleLatLng(journey.SnappedLinePoints);
        //}
        //else {
        //    latLngArray = this.plotService.convertPlotsToGoogleLatLng(journey.plots);
        //}

        var linePoints = journey.SnappedLinePoints && journey.SnappedLinePoints.length ? journey.SnappedLinePoints : journey.LinePoints;

        if (linePoints && linePoints.length !== 0) {
            latLngArray = this.mapService.convertArrayToGoogleLatLng(linePoints);
        }
        else if (journey.plots) {
            latLngArray = this.mapService.convertPlotsToGoogleLatLng(journey.plots);
        }
        else {
            console.error("No data to create journey polyline from", journey);
            return null;
        }

        return this.mapService.createPolyline(journey.Id, latLngArray, journey.lineColor, journeyEvents, dashed);
    }

    private getJourneyDateRange(startDate: Date, endDate?: Date): IDateParameters {

        const startMoment = this.veriDate.createMoment(startDate).startOf("day");
        let endMoment = this.veriDate.createMoment(startDate).add(1, "day").startOf("day");

        if (endDate && !this.veriDate.isSameDate(startDate, endDate)) {
            endMoment = this.veriDate.createMoment(endDate);
        }

        const params: IDateParameters = {
            start: startMoment.utc().format(),
            end: endMoment.utc().format()
        };

        return params;
    }

    mapVehicleJourneys(journeys: IJourney[], vehicles: IVehicle[]): IVehicleJourneysMap[] {
        var map: IVehicleJourneysMap[] = [];

        vehicles.forEach(vehicle => {
            const matchingVehicleJourneysMap = {
                id: vehicle.Id,
                vehicle: vehicle,
                journeysByDay: [],
                journeys: [],
                visibleJourneys: [],
                megaJourney: null,
                journeyCount: 0
            };

            matchingVehicleJourneysMap.journeys = journeys.filter(j => j.VehicleId == vehicle.Id);

            if (matchingVehicleJourneysMap.journeys.length == 0) {
                return;
            }

            map.push(matchingVehicleJourneysMap);
            matchingVehicleJourneysMap.journeys.sort((x, y) => this.veriDate.sort(x.StartUtc, y.StartUtc, SortDirection.Descending));

            var matchingVehicleJourneyDayMap: IVehicleJourneyByDateMap = null;

            matchingVehicleJourneysMap.journeys.forEach(journey => {
                var momentDate = this.veriDate.createMoment(journey.StartUtc);
                //console.log(journey.StartUtc);

                matchingVehicleJourneysMap.journeysByDay.forEach(vehicleJourneyByDateMap => {
                    if (momentDate.isSame(this.veriDate.createMoment(vehicleJourneyByDateMap.date), "days")) {
                        matchingVehicleJourneyDayMap = vehicleJourneyByDateMap;
                    }
                });

                if (!matchingVehicleJourneyDayMap) {
                    matchingVehicleJourneyDayMap = {
                        date: momentDate.toDate(),
                        journeys: []
                    };

                    matchingVehicleJourneysMap.journeysByDay.push(matchingVehicleJourneyDayMap);
                }

                journey.vehicle = matchingVehicleJourneysMap.vehicle;

                matchingVehicleJourneyDayMap.journeys.push(journey);
                matchingVehicleJourneysMap.journeys.push(journey);

                if (!matchingVehicleJourneysMap.megaJourney) {
                    matchingVehicleJourneysMap.megaJourney = copy(journey);

                    // Colours journey by vehicle
                    matchingVehicleJourneysMap.megaJourney.indexColor = this.colourService.getColourIndex(matchingVehicleJourneysMap.megaJourney.VehicleId, true);
                    matchingVehicleJourneysMap.megaJourney.lineColor = this.colourService.getColour(matchingVehicleJourneysMap.megaJourney.VehicleId, true).solidColour;

                    if (!matchingVehicleJourneysMap.megaJourney.LinePoints) {
                        matchingVehicleJourneysMap.megaJourney.LinePoints = []
                    }
                }
                else if (journey.LinePoints) {
                    matchingVehicleJourneysMap.megaJourney.LinePoints = matchingVehicleJourneysMap.megaJourney.LinePoints.concat(journey.LinePoints);
                }
            });


            //map.forEach(vehicleMap => {
            var journeyCount = 0;

            matchingVehicleJourneysMap.journeysByDay.forEach(mapByDay => {
                journeyCount += mapByDay.journeys.length;
            });

            matchingVehicleJourneysMap.journeyCount = journeyCount;
            matchingVehicleJourneysMap.journeysByDay.sort((x, y) => {
                if (x.date > y.date) {
                    return -1;
                }

                if (x.date < y.date) {
                    return 1;
                }

                if (x.date === y.date) {
                    return 0;
                }
            });
            //});
        });

        return map;
    }

    updateTotalElapsedMinutesForJourney(journey: IJourney): void {
        if (!journey) {
            return;
        }

        journey.totalElapsedMinutes = this.veriDate.createMoment(journey.EndUtc).diff(this.veriDate.createMoment(journey.StartUtc), "minutes");
    }

    updateStartMinuteOfDayForJourney(journey: IJourney): void {
        if (!journey) {
            return;
        }

        const startMoment = this.veriDate.createMoment(journey.StartUtc);
        const startMinute = (startMoment.hour() * 60) + startMoment.minute();
        journey.startMinuteOfDay = startMinute;
    }

    updateEndMinuteOfDayForJourney(journey: IJourney): void {
        if (!journey) {
            return;
        }

        const endMoment = this.veriDate.createMoment(journey.EndUtc);
        const startMinute = (endMoment.hour() * 60) + endMoment.minute();
        journey.endMinuteOfDay = startMinute;
    }

    getLatLngsForJourneys(journeys: IJourney[]) {
        const latLngs: google.maps.LatLng[] = [];

        journeys.forEach(journey => {
            if (!journey?.LinePoints) {
                latLngs.push(new google.maps.LatLng(journey.StartLatitude, journey.StartLongitude));
                latLngs.push(new google.maps.LatLng(journey.EndLatitude, journey.EndLongitude));
            } else {
                journey.LinePoints.forEach(point => {
                    latLngs.push(new google.maps.LatLng(point[0], point[1]));
                });
            }
        });

        return latLngs;
    }

    updateJourneyExpense(journey: IJourney): angular.IPromise<void> {
        return this
            .$http({
                method: "POST",
                url: `/api/journeys/expense`,
                data: journey
            })
            .then((result: angular.IHttpResponse<void>) => {
                return result.data;
            });
    }

    updateJourneysExpenses(journeys: IJourney[]): angular.IPromise<void> {
        return this
            .$http({
                method: "POST",
                url: `/api/journeys/expenses`,
                data: journeys
            })
            .then((result: angular.IHttpResponse<void>) => {
                return result.data;
            });
    }

    mapJourneysToResources(journeys: IJourney[], vehicles: IVehicle[], drivers: IDriver[]) {
        journeys.forEach(journey => {

            var journeyVehicles = vehicles.filter(vehicle => vehicle.Id == journey.VehicleId)
            var journeyDrivers = drivers.filter(driver => driver.Id == journey.DriverId)

            if (journeyVehicles.length) {
                journey.vehicle = journeyVehicles[0]
            }

            if (journeyDrivers.length) {
                journey.driver = journeyDrivers[0]
            }
        })
    }

    filterJourneys(journeys: IJourney[], vehicleFilter: IVehicleFilter, driverFilter: IDriverFilter, groupFilter: IGroupFilter): IJourney[] {

        if ((!vehicleFilter || !vehicleFilter.vehicle) && (!driverFilter || !driverFilter.driver) && (!groupFilter || !groupFilter.group)) {
            return journeys;
        }

        if (vehicleFilter && vehicleFilter.vehicle) {
            journeys = journeys.filter(v => v.VehicleId === vehicleFilter.vehicle.Id);
        }

        if (driverFilter && driverFilter.driver) {
            journeys = journeys.filter(v => v.DriverId === driverFilter.driver.Id);
        }

        if (groupFilter && groupFilter.group) {
            journeys = this.filterJourneysByGroup(journeys, groupFilter);
        }

        return journeys;
    }

    private filterJourneysByGroup(journeysToFilter: IJourney[], filter: IGroupFilter) {
        var filteredVideos: IJourney[] = [];

        journeysToFilter.forEach(v => {
            if (filter.group.DriverIds.find(driverId => driverId === v.DriverId) && !filteredVideos.find(fv => fv.Id === v.Id)) {
                filteredVideos.push(v);
            }

            if (filter.group.VehicleIds.find(vehicleId => vehicleId === v.VehicleId) && !filteredVideos.find(fv => fv.Id === v.Id)) {
                filteredVideos.push(v);
            }
        });

        return filteredVideos;
    }

    // ----------------------

    private createAdditionalJourneyEvents(allJourneyEvents: IPlotEvent[], vehicle: IVehicle, plots: IPlot[], commonEvents: IEvent[], trailerAssociations: IVehicleTrailerMapping[]) {
        // Setup speeding events
        commonEvents.forEach(commonEvent => {
            // In testing this happens ...
            if (commonEvent.VehicleId != vehicle.Id) return;

            const plot = this.plotService.findPlotById(commonEvent.StartPlotId, plots);
            if (plot) {

                const label = commonEvent.Type == ReasonCode.ContextualSpeeding
                    ? this.translationService.t("journeyService.speeding")
                    : this.translationService.tEnum(ReasonCode, 'ReasonCode', commonEvent.Type);

                allJourneyEvents.push(this.googleMarkerService.createEventMarker(plot, { label, markerClass: "marker-event-driver-changed", subIcon: "warning" }));
            }
        });

        // Setup associations as events
        //console.log("trailerAssociations", trailerAssociations);
        //trailerAssociations.forEach(trailerAssociation => {
        //    var startPlot = this.plotService.findPlotClosestToTime(moment(trailerAssociation.StartUtc), plots);
        //    var endPlot = this.plotService.findPlotClosestToTime(moment(trailerAssociation.EndUtc), plots);

        //    var startMarker = this.googleMarkerService.createEventMarker(startPlot, "Trailer " + trailerAssociation.TrailerId + " Attached", "marker-event-trailer-association", "call_merge")
        //    var endMarker = this.googleMarkerService.createEventMarker(endPlot, "Trailer " + trailerAssociation.TrailerId + " Detached", "marker-event-trailer-association", "call_split")

        //    allJourneyEvents.push(startMarker);
        //    allJourneyEvents.push(endMarker);
        //});

        // If we are missing an ignition on, create a fake one
    }

    private mapDataToJourney(journey: IJourney, plots: IPlot[], allJourneyEvents: IPlotEvent[]): angular.IPromise<void> {
        journey.plots = this.plotService.slicePlotArrayByTime(plots, journey.StartUtc, journey.EndUtc);

        // Color plots
        journey.plots.forEach(plot => {
            plot.color = journey.lineColor;
        });

        this.plotEventService.setJourneyEvents(allJourneyEvents, journey);

        var startEvent: IPlotEvent = null;
        var stopEvent: IPlotEvent = null;

        journey.events.forEach(event => {
            if (event.label == "Start") { startEvent = event; }
            if (event.label == "Stop") { stopEvent = event; }
            
        });

        if (!startEvent && journey.plots.length) {
            startEvent = this.plotEventService.createJourneyStartStopEvent(journey.plots[0], "Start");
            startEvent.journey = journey;

            allJourneyEvents.push(startEvent);
            journey.events.unshift(startEvent);
        }

        if (startEvent && !startEvent.plot?.LocationInformation) {
            startEvent.plot.LocationInformation = journey?.StartLocationInformation;
        }

        let getPlot = this.$q.resolve();

        if (!stopEvent && !journey.isCurrent) {
            getPlot = this.plotService.getPlot(journey.VehicleId, journey.EndPlotId).then(plot => {
                stopEvent = this.plotEventService.createJourneyStartStopEvent(plot, "Stop");
                stopEvent.journey = journey;

                allJourneyEvents.push(stopEvent);
                journey.events.push(stopEvent);
            });
        }

        if (stopEvent && !stopEvent.plot?.LocationInformation) {
            stopEvent.plot.LocationInformation = journey?.EndLocationInformation;
        }

        journey.DriverId = this.findJourneyDriver(journey.plots);

        if (journey.DriverId) {
            journey.driver = this.driverService.getCachedDriver(journey.DriverId);
        }

        if (journey.isCurrent && plots.length > 0) {
            // Create current position marker for current journey
            var latestPlot = plots[plots.length - 1];
            if (latestPlot.Reason !== ReasonCode.IgnitionOn) {
                const marker = this.googleMarkerService.createEventMarker(
                    latestPlot,
                    {
                        label: this.translationService.t("journeyService.currentPosition"),
                        markerClass: "marker-event-current-position",
                        subIcon: "directions_car",
                        showLabel: true,
                        eventType: EventMarkerType.CurrentPosition
                    });

                if (journey) {
                    journey.events = journey.events || [];
                    journey.events.push(marker);
                }
            }

            journey.DistanceKm = journey.DistanceKm ?? journey.Distance;
        }

        return getPlot;
    }

    colorJourney(journey: IJourney, index: number) {
        journey.indexColor = this.colourService.getColourIndex(index, true);
        journey.lineColor = this.colourService.getColour(index, true).solidColour;
    }

    private createLatLngsForPlots(plots: IPlot[]): google.maps.LatLng[] {
        const path: google.maps.LatLng[] = [];

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            // Ignore these plot types
            if (//plot.Reason === ReasonCode.IgnitionOn ||
                //plot.Reason === ReasonCode.IgnitionOff ||
                //plot.Reason === ReasonCode.FmsEngineOn ||
                //plot.Reason === ReasonCode.FmsEngineOff ||
                //plot.Reason === ReasonCode.TravelSend ||
                plot.Reason === ReasonCode.Wakeup) {
                continue;
            }

            path.push(new google.maps.LatLng(plot.Latitude, plot.Longitude));
        }

        return path;
    }

    private randomNumber(min: number, max: number) {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

    checkIfJourneysInMapBounds(veriMap: IVeriMapComponent, journeys: IJourney[], filterOutStartMinute: number = null, filterOutEndMinute: number = null): void {
        if (!veriMap || !veriMap?.mapInitialised) {
            return;
        }

        const mapBounds: google.maps.LatLngBounds = veriMap.getMap().getBounds();

        journeys.forEach(journey => {
            journey.visibleInBounds = false;

            if (!journey.LinePoints) {
                return;
            }

            for (var i = 0; i < journey.LinePoints.length; i++) {
                const point = journey.LinePoints[i];

                if (mapBounds.contains(new google.maps.LatLng(point[0], point[1]))) {
                    journey.visibleInBounds = (filterOutStartMinute && filterOutEndMinute) ? (filterOutStartMinute < filterOutEndMinute) : true;
                    break;
                }
            }
        });
    }

    sortJourneysByInMapBounds(journeys: IJourney[]): void {
        if (journeys?.length > 0) {
            journeys.sort((journey1, journey2) => (journey1.visibleInBounds === journey2.visibleInBounds) ? 0 : journey1.visibleInBounds ? -1 : 1);
        }
    }

    sortJourneysByStartTime(journeys: IJourney[]): void {
        if (journeys?.length > 0) {
            journeys.sort((journey1, journey2) => this.veriDate.sort(journey1.StartUtc, journey2.StartUtc, SortDirection.Ascending));
        }
    }

    filterJourneysByTime(journeys: IJourney[], minuteBufferEitherSide: number = 0, filterOutStartMinute: number, filterOutEndMinute: number): IJourney[] {
        if (!journeys) {
            return;
        }

        const timeFilterFromMinutes: number = filterOutStartMinute - minuteBufferEitherSide;
        const timeFilterEndMinutes: number = filterOutEndMinute + minuteBufferEitherSide;

        return journeys.filter(journey => {
            const startMoment: moment.Moment = this.veriDate.createMoment(journey.StartUtc);
            const endMoment: moment.Moment = this.veriDate.createMoment(journey.EndUtc);

            const startMinuteOfDay: number = (startMoment.hour() * 60) + startMoment.minute();
            const endMinuteOfDay: number = (endMoment.hour() * 60) + endMoment.minute();

            return startMinuteOfDay < timeFilterEndMinutes && endMinuteOfDay > timeFilterFromMinutes;
        });
    }

    attachSensorDataToPlots(plots: IPlot[], sensorsData: ISensorData[]) {
        plots.forEach((plot) => {
            const sensorData = sensorsData.find((sensorData) => sensorData.PlotId === plot.Id);
            if (sensorData) { plot.SensorData = sensorData }
        });
    }

    private getLastJourneyEndUtcDate(journeys: IJourney[]): Date {
        if (!journeys?.length) {
            return;
        }

        const endUtc = journeys[journeys.length - 1].EndUtc;

        if (!endUtc) {
            return;
        }

        return this.veriDate.createMoment(endUtc).toDate();
    }
}
